import * as url from "../helpers/url_helper";
import { getResourceStore } from "./crudFactory";
import { MESSAGE_CATEGORIES } from "../helpers/messageContract";
import { NOTIFY_CATEGORY_BY_TAB } from "../constants/notifyCategories";

/**
 * DcNotificationRule mock(各類訊息設定;2026-10-05 第二批 A 方案)——「mock 跟 API 一模一樣」。
 *
 * ## 規則:投影自既有 `notify-rule` store(與 DcContract 讀取側同一套作法)
 * 新增／編輯精靈還在 mock(只在 development),寫進的是舊 `/api/notify-rules`;讀取側若另開一份資料,
 * 精靈建的規則會在列表上消失。所以 GetAll／GetById 把 store 列**投影成 `DcNotificationRuleDto` 47 鍵**,
 * SetPaused／Delete 也寫回同一個 store。
 * 值語意依 **10/5 SIT round-trip 實測**(`probeNotifyRuleRoundTrip`,Create→GetById→SetPaused→Delete):
 * - Create 直接 `Active`(不送審);SetPaused 寫 `effective_on`
 * - **Delete 是軟刪**:status→`Voided`(已作廢)、GetById 仍讀得到;預設 GetAll 不列、`status=Voided` 才列;分類計數不算
 * - 管道文字 `站內．Email`(全形點);沒開的階 `tierN_channels`＝`None`、文字 null
 * - **GetAll 的 triggers 一律 []**,GetById 才有內容
 *
 * ## 系統事件:抄 10/5 SIT 實測(GetSystemEvents 6 項,匿名唯讀 GET 取得)
 */
const CATEGORY_ROWS = MESSAGE_CATEGORIES.map((c) => ({
  value: c.value, enumName: c.enumName, text: NOTIFY_CATEGORY_BY_TAB[c.tab],
}));
const ENUM_BY_TEXT = Object.fromEntries(CATEGORY_ROWS.map((c) => [c.text, c.enumName]));
const TEXT_BY_ENUM = Object.fromEntries(CATEGORY_ROWS.map((c) => [c.enumName, c.text]));
const STATUS_TEXT = { Active: "啟用", Paused: "暫停", UnderReview: "審核中", Voided: "已作廢" };

// 各類第一個觸發條件的 trigger_key(10/5 smoke GetTriggerOptions 實測);投影時帶一筆,讓 triggers[] 的形狀被守衛驗到
const FIRST_TRIGGER_KEY = {
  DataIncomplete: "Asset.Room", Expiry: "Contract.Expiry", LowStock: "Capacity.RackU",
  Approval: "Approval.Create", Alert: "Alert.Connectivity", VersionUpdate: "Version.NewSoftware",
};
const triggerOf = (category, text) => ({
  trigger_key: FIRST_TRIGGER_KEY[category] || null,
  text: text || null,
  note: null,
  threshold_text: null,
  value_label: null,
  value_editable: false,
  tier1_value: 0,
  tier2_value: 0,
  tier3_value: 0,
  threshold_label: null,
  tier1_threshold: 0,
  tier2_threshold: 0,
  tier3_threshold: 0,
});

const statusOf = (row) => {
  if (row.voided) return "Voided";
  if (row.approvalStatus === "pending") return "UnderReview";
  return row.active ? "Active" : "Paused";
};
const firstOr = (arr, i) => (Array.isArray(arr) && arr[i] ? arr[i] : null);
const restOf = (arr) => (Array.isArray(arr) && arr.length > 1 ? arr.slice(1).join("；") : null);

// 10/5 實測:InApp, Email → 「站內．Email」(全形點)
const CHANNEL_TEXT = "站內．Email";

const SEED_MANAGER_ID = 1;
const seedManagerName = (id) => {
  const u = (getResourceStore("user")?.all() || []).find((r) => String(r.id) === String(id));
  return u ? u.name : null;
};

/** 精靈 Create／Update 存進來的列帶整份 DTO(`__dto`);投影時以它為準,狀態仍看 store 欄位 */
const fromStoredDto = (row, withTriggers) => {
  const status = statusOf(row);
  return {
    ...row.__dto,
    triggers: withTriggers ? row.__dto.triggers || [] : [],
    status,
    status_text: STATUS_TEXT[status],
    effective_on: row.effectiveOn || row.__dto.effective_on || null,
    id: row.id,
  };
};

/** store 列(前端形狀)→ DcNotificationRuleDto(47 鍵,照 swagger;值照 10/5 實測)。`withTriggers` false＝列表(實測 []) */
export function ruleRowToDto(row, { withTriggers = true } = {}) {
  if (row.__dto) return fromStoredDto(row, withTriggers);
  const category = ENUM_BY_TEXT[row.category] || null;
  const status = statusOf(row);
  const d = row.display || {};
  const stages = row.stages || [];
  const stageOn = (level) => !!(stages.find((s) => s.level === level) || {}).enabled;
  return {
    name: row.name || null,
    category,
    category_text: row.category || null,
    sub_type_key: row.subTypeKey || null,
    // SIT 的規則一律有管理者(建立時必帶,10/5 實測 manager_customer_id=8);種子沒寫的補成 1 號,
    // 否則編輯回填對不到人、精靈的管理者必填會卡在第一步 —— mock 不能比真後端還「空」
    manager_customer_id: Number(row.managerId) || SEED_MANAGER_ID,
    manager_customer_name: row.manager || seedManagerName(Number(row.managerId) || SEED_MANAGER_ID),
    delivery_mode: "以帳號方式彙整整批發送",
    time_unit: (row.cadence && { day: "天", hour: "小時", minute: "分鐘" }[row.cadence.unit]) || null,
    applicable_modules: null,
    detection_interval_minutes: 0,
    triggers: withTriggers && category ? [triggerOf(category, firstOr(d.trigger, 0))] : [],
    first_trigger_offset: 0,
    tier1_second_offset: 0,
    tier1_repeat_interval: 0,
    tier1_stop_on_resolved: true,
    tier1_send_closing_message: false,
    tier1_channels: "InApp, Email",
    tier1_channels_text: CHANNEL_TEXT,
    tier2_enabled: stageOn(2),
    tier2_mode: 0,
    tier2_value: 0,
    tier2_channels: stageOn(2) ? "InApp, Email" : "None",
    tier2_channels_text: stageOn(2) ? CHANNEL_TEXT : null,
    tier3_enabled: stageOn(3),
    tier3_mode: 0,
    tier3_value: 0,
    tier3_channels: stageOn(3) ? "InApp, Email" : "None",
    tier3_channels_text: stageOn(3) ? CHANNEL_TEXT : null,
    status,
    status_text: STATUS_TEXT[status],
    is_system_default: false,
    approval_request_no: status === "UnderReview" ? row.docNo || null : null,
    effective_on: row.effectiveOn || null,
    created_on: row.createdAt || "2026-08-01T09:00:00",
    updated_on: row.updatedAt || null,
    updated_by_customer_id: 0,
    updated_by_customer_name: row.updatedBy || null,
    sub_type_text: row.subTypeText || null,
    trigger_summary_text: firstOr(d.trigger, 0),
    trigger_note_text: restOf(d.trigger),
    tier1_summary_text: firstOr(d.firstFreq, 0),
    tier1_note_text: restOf(d.firstFreq),
    escalation_summary_text: firstOr(d.escalation, 0),
    escalation_note_text: restOf(d.escalation),
    channels_text: CHANNEL_TEXT,
    applicable_modules_text: null,
    id: row.id,
  };
}

/** 10/5 SIT 實測(GetSystemEvents) */
const systemEventsSeed = () => [
  { event_key: "Approval.Shared", event_group: "審核流程共用訊息", trigger_timing: "送審／進關／核准／退回／撤回", recipient: "發動者・各關簽核人", in_app_enabled: true, in_app_applicable: true, email_enabled: false, enabled: true, display_order: 0, id: 1 },
  { event_key: "BatchImport.Result", event_group: "批次匯入結果", trigger_timing: "匯入結案（全數成功／整份退回）", recipient: "發動者", in_app_enabled: true, in_app_applicable: true, email_enabled: false, enabled: true, display_order: 1, id: 2 },
  { event_key: "Account.Password", event_group: "帳號與密碼", trigger_timing: "忘記密碼／重設完成／帳號建立／登入鎖定", recipient: "該使用者", in_app_enabled: false, in_app_applicable: false, email_enabled: false, enabled: true, display_order: 2, id: 3 },
  { event_key: "Dispatch.StatusChanged", event_group: "派工狀態異動", trigger_timing: "報修中／派工中／已修復", recipient: "資產管理者・原報修人", in_app_enabled: true, in_app_applicable: true, email_enabled: false, enabled: true, display_order: 3, id: 4 },
  { event_key: "Owner.Changed", event_group: "管理者／負責人變更", trigger_timing: "工作轉派核准生效／資產管理者變更", recipient: "新舊負責人", in_app_enabled: true, in_app_applicable: true, email_enabled: false, enabled: true, display_order: 4, id: 5 },
  { event_key: "Delivery.Failed", event_group: "通知發送失敗", trigger_timing: "Email／簡訊閘道重試 3 次仍失敗", recipient: "系統管理者", in_app_enabled: true, in_app_applicable: true, email_enabled: false, enabled: true, display_order: 5, id: 6 },
];
const EVENT_KEYS = Object.keys(systemEventsSeed()[0]);

// —— Create／Update:後端算的列表摘要,mock 照 10/5 實測能抄的抄、抄不到的照 profile 的 valueLabel 推 ——
const PROFILE_LABEL = {
  DataIncomplete: { 1: null, 2: "第一階通知後 ＋{0} 天", 3: "第一階通知後 ＋{0} 天" },
  "Expiry/Contract": { 1: "到期前 {0} 天首次", 2: "到期前 {0} 天", 3: "到期前 {0} 天" },
  "Expiry/Schedule": { 1: "維護日前 {0} 天", 2: "維護日前 {0} 天", 3: "維護日前 {0} 天" },
  "Expiry/Lifecycle": { 1: "低 365／中 180／高 90／嚴重 30", 2: "嚴重剩餘 ≤ {0} 天", 3: "逾期剩餘 ≤ {0} 天" },
  "Expiry/Depreciation": { 1: "攤提結束日前 {0} 天", 2: "攤提結束日前 {0} 天", 3: "攤提結束日前 {0} 天" },
  LowStock: { 1: "使用率 ≥ {0}%", 2: "使用率 ≥ {0}%", 3: "使用率 ≥ {0}%" },
  Approval: { 1: "每關卡 {0} 小時", 2: "逾 {0} 倍", 3: "逾 {0} 倍" },
  Alert: { 1: "每 {0} 分鐘偵測", 2: "持續 {0} 分鐘未恢復", 3: "持續 {0} 分鐘未恢復" },
  VersionUpdate: { 1: "每 {0} 天", 2: "逾 {0} 天", 3: "逾 {0} 天" },
};
const fill = (tpl, v) => (tpl ? tpl.replace("{0}", v) : null);
const channelText = (c) => {
  const s = String(c || "");
  const parts = [/InApp/.test(s) ? "站內" : null, /Email/.test(s) ? "Email" : null].filter(Boolean);
  return parts.length ? parts.join("．") : null; // 10/5 實測「站內．Email」
};
const TEXT_BY_CATEGORY = Object.fromEntries(CATEGORY_ROWS.map((c) => [c.enumName, c.text]));
const SUBTYPE_TEXT = { Contract: "契約期限類型", Schedule: "週期排程類型", Lifecycle: "原廠生命週期", Depreciation: "財務攤提" };

/** 精靈送來的 DTO → 後端會補上的文字欄位(摘要、名稱) */
export function computeRuleTexts(dto, managerName) {
  const pk = dto.category === "Expiry" ? `Expiry/${dto.sub_type_key}` : dto.category;
  const L = PROFILE_LABEL[pk] || {};
  const triggers = dto.triggers || [];
  const tierText = (i) => (dto[`tier${i}_enabled`] ? `${i}階:${fill(L[i], dto[`tier${i}_value`])}` : null);
  const esc = [tierText(2), tierText(3)].filter(Boolean);
  return {
    category_text: TEXT_BY_CATEGORY[dto.category] || null,
    sub_type_text: dto.sub_type_key ? SUBTYPE_TEXT[dto.sub_type_key] || null : null,
    manager_customer_name: managerName || null,
    delivery_mode: "以帳號方式彙整整批發送",
    // 10/5 實測:trigger 摘要＝子項文字、補充「共 N 項觸發條件」
    trigger_summary_text: triggers.map((t) => t.text || t.trigger_key).join("、") || null,
    trigger_note_text: triggers.length ? `共 ${triggers.length} 項觸發條件` : null,
    // 10/5 實測:資料未填 → 「D＋15 天」／「D＝填寫完成日期」
    tier1_summary_text: dto.category === "DataIncomplete" ? `D＋${dto.first_trigger_offset} 天` : fill(L[1], dto.first_trigger_offset),
    tier1_note_text: dto.category === "DataIncomplete" ? "D＝填寫完成日期" : (dto.tier1_second_offset ? `再次＝前 ${dto.tier1_second_offset} 天` : null),
    // 10/5 實測:二三階都沒開 → null
    escalation_summary_text: esc[0] || null,
    escalation_note_text: esc[1] || null,
    tier1_channels_text: channelText(dto.tier1_channels),
    tier2_channels_text: dto.tier2_enabled ? channelText(dto.tier2_channels) : null,
    tier3_channels_text: dto.tier3_enabled ? channelText(dto.tier3_channels) : null,
    channels_text: channelText(dto.tier1_channels),
    applicable_modules_text: dto.applicable_modules || null,
  };
}

// GetTriggerOptions(10/5 實測的 trigger_key;文字資料未填抄實測,其餘以 key 代替 —— 前端只拿它當 triggers[] 的底)
const TRIGGER_OPTIONS = {
  DataIncomplete: [["Asset.Room", "資產管理›機房"], ["Asset.Rack", "資產管理›機櫃"], ["Asset.Hardware", "資產管理›硬體"], ["Asset.Software", "資產管理›軟體"],
    ["Ops.Contract", "營運資訊›合約"], ["Ops.Project", "營運資訊›專案"], ["Ops.Vendor", "營運資訊›廠商"], ["Ops.Ip", "營運資訊›IP"]],
  "Expiry/Contract": [["Contract.Expiry"], ["Project.Expiry"]],
  "Expiry/Schedule": [["Contract.MaintenanceSchedule"]],
  "Expiry/Lifecycle": [["Asset.Eos"], ["Asset.Eol"], ["Asset.EolEosOverdue"]],
  "Expiry/Depreciation": [["Asset.DepreciationEnd"]],
  LowStock: [["Capacity.Room"], ["Capacity.RackU"], ["Capacity.RackPdu"], ["Capacity.RackSubUnit"]],
  Approval: [["Approval.Create"], ["Approval.Update"], ["Approval.Delete"]],
  Alert: [["Alert.Connectivity"], ["Alert.Hardware"], ["Alert.SystemResource"], ["Alert.PortStatus"], ["Alert.Application"]],
  VersionUpdate: [["Version.NewSoftware"], ["Version.NewFirmware"], ["Version.NewCve"], ["Version.KevDue"], ["Version.ScanDue"], ["Version.ScraperSynced"]],
};

export default function registerDcNotificationRule(mock, { withAuth, toPageEnvelope }) {
  const B = url.DCK_BACKEND.NOTIFICATION_RULE;
  const rules = () => getResourceStore("notify-rule");
  let events = systemEventsSeed();
  const idOf = (config) => Number(config.url.split("?")[0].split("/").pop());
  const queryOf = (config) => {
    const qs = (config.url.split("?")[1] || "");
    return { ...Object.fromEntries(new URLSearchParams(qs)), ...(config.params || {}) };
  };

  mock.onGet(`${B}/GetAll`).reply(withAuth((config) => {
    const p = config.params || {};
    let rows = rules().all().map((r) => ruleRowToDto(r, { withTriggers: false }));
    if (p.category) rows = rows.filter((r) => r.category === p.category);
    // 實測:預設不列已作廢(軟刪),要 status=Voided 才列
    rows = p.status ? rows.filter((r) => r.status === p.status) : rows.filter((r) => r.status !== "Voided");
    const kw = String(p.keyword || "").trim().toLowerCase();
    if (kw) rows = rows.filter((r) => [r.name, r.category_text].some((v) => String(v || "").toLowerCase().includes(kw)));
    const pageIndex = Number(p.pageIndex) || 0;
    const pageSize = Number(p.pageSize) || 2147483647;
    return [200, toPageEnvelope(rows, pageIndex, pageSize)];
  }));

  // 實測形狀 [{value:1..6,text,count}](swagger 沒寫型別);不吃參數
  mock.onGet(`${B}/GetCategoryCounts`).reply(withAuth(() => {
    const dtos = rules().all().map((r) => ruleRowToDto(r)).filter((r) => r.status !== "Voided");
    return [200, CATEGORY_ROWS.map((c) => ({ value: c.value, text: TEXT_BY_ENUM[c.enumName], count: dtos.filter((r) => r.category === c.enumName).length }))];
  }));

  mock.onGet(new RegExp(`${B}/GetById/\\d+$`)).reply(withAuth((config) => {
    const row = rules().find(idOf(config));
    return row ? [200, ruleRowToDto(row)] : [404, "查無通知規則"];
  }));

  // SetPaused?id&paused(paused 預設 true,swagger);不送審,直接生效;審核中的不能動
  mock.onPost(new RegExp(`${B}/SetPaused`)).reply(withAuth((config) => {
    const q = queryOf(config);
    const id = Number(q.id);
    if (!id) return [400, { title: "One or more validation errors occurred.", errors: { id: ["The id field is required."] } }];
    const row = rules().find(id);
    if (!row) return [404, "查無通知規則"];
    // ⚠️ 審核中／已作廢能不能暫停,實測沒驗到;mock 先擋(前端本來就不給這兩種狀態暫停鈕)
    if (row.approvalStatus === "pending" || row.voided) return [400, "此規則目前無法暫停或恢復"];
    const paused = q.paused === undefined ? true : String(q.paused) !== "false";
    return [200, ruleRowToDto(rules().patch(id, { active: !paused, effectiveOn: new Date().toISOString() }))];
  }));

  mock.onDelete(new RegExp(`${B}/Delete/\\d+$`)).reply(withAuth((config) => {
    const id = idOf(config);
    if (!rules().find(id)) return [404, "查無通知規則"];
    // 實測:軟刪 → 已作廢(GetById 仍讀得到)
    rules().patch(id, { voided: true });
    return [200, ""];
  }));

  mock.onGet(`${B}/GetTriggerOptions`).reply(withAuth((config) => {
    const p = config.params || {};
    if (!p.category) return [400, { title: "One or more validation errors occurred.", errors: { category: ["The category field is required."] } }];
    const list = TRIGGER_OPTIONS[p.category === "Expiry" ? `Expiry/${p.subTypeKey}` : p.category] || [];
    return [200, list.map(([trigger_key, text]) => ({ ...triggerOf(null, text || trigger_key), trigger_key }))];
  }));

  // Create／Update:10/5 實測 Create 直接 Active、不送審。只收 swagger 有的鍵(DTO 47 鍵)
  const DTO_KEYS = Object.keys(ruleRowToDto({ id: 0, category: "資料未填" }));
  const managerNameOf = (id) => {
    const u = (getResourceStore("user")?.all() || []).find((r) => String(r.id) === String(id));
    return u ? u.name : null;
  };
  const validate = (b) => {
    if (!b || !String(b.name || "").trim()) return "規則名稱為必填";
    if (!CATEGORY_ROWS.some((c) => c.enumName === b.category)) return "訊息種類不正確";
    if (b.category === "Expiry" && !b.sub_type_key) return "到期類型需選擇子類";
    return null;
  };
  const toStored = (b) => {
    const picked = Object.fromEntries(DTO_KEYS.filter((k) => k in b).map((k) => [k, b[k]]));
    return { ...picked, ...computeRuleTexts(picked, managerNameOf(picked.manager_customer_id)) };
  };
  mock.onPost(`${B}/Create`).reply(withAuth((config) => {
    const b = JSON.parse(config.data || "{}");
    const bad = validate(b);
    if (bad) return [400, bad];
    const dto = toStored(b);
    const row = rules().add({ name: dto.name, category: dto.category_text, active: true, approvalStatus: "approved", __dto: dto });
    return [200, ruleRowToDto(rules().find(row.id))];
  }));
  mock.onPut(`${B}/Update`).reply(withAuth((config) => {
    const b = JSON.parse(config.data || "{}");
    const row = b.id ? rules().find(b.id) : null;
    if (!row) return [404, "查無通知規則"];
    if (row.voided) return [400, "此規則已作廢"];
    const bad = validate(b);
    if (bad) return [400, bad];
    const dto = toStored(b);
    rules().patch(b.id, { name: dto.name, category: dto.category_text, __dto: dto });
    return [200, ruleRowToDto(rules().find(b.id))];
  }));

  mock.onGet(`${B}/GetSystemEvents`).reply(withAuth(() => [200, events.map((e) => ({ ...e }))]));

  // body＝DcSystemEventSettingDto[];只收 swagger 有的鍵、依 event_key 對回
  mock.onPost(`${B}/SaveSystemEvents`).reply(withAuth((config) => {
    let body;
    try { body = JSON.parse(config.data || "[]"); } catch { return [400, "資料格式有誤"]; }
    if (!Array.isArray(body)) return [400, "資料格式有誤"];
    const byKey = Object.fromEntries(body.map((e) => [e.event_key, e]));
    events = events.map((e) => {
      const incoming = byKey[e.event_key];
      if (!incoming) return e;
      const picked = Object.fromEntries(EVENT_KEYS.filter((k) => k in incoming).map((k) => [k, incoming[k]]));
      // 站內不適用的事件,站內開關後端不收
      return { ...e, ...picked, in_app_applicable: e.in_app_applicable, in_app_enabled: e.in_app_applicable ? !!picked.in_app_enabled : e.in_app_enabled, event_key: e.event_key, id: e.id };
    });
    return [200, events.map((e) => ({ ...e }))];
  }));
}
